import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthShell from "@/components/AuthShell";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth, startGoogleLogin } from "@/context/AuthContext";
import { useI18n } from "@/i18n";

function GoogleButton() {
  const { t } = useI18n();
  const start = async () => {
    // Branded (operator's own Google OAuth client) when configured,
    // otherwise fall back to the Emergent-managed sign-in.
    try {
      const { data } = await api.get("/auth/google/config");
      if (data.mode === "custom") {
        const redirectUri = window.location.origin + "/auth/google";
        window.location.href = `/api/auth/google/start?redirect_uri=${encodeURIComponent(redirectUri)}`;
        return;
      }
    } catch (e) {}
    startGoogleLogin();
  };
  return (
    <button
      type="button"
      onClick={start}
      data-testid="login-google"
      className="flex w-full items-center justify-center gap-3 rounded-xl border border-line bg-surface px-4 py-3 text-sm font-bold text-ink transition-colors hover:border-accent"
    >
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z" />
        <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.5l5.6 4.4c3.3-3 5.4-7.4 5.4-12 0-1.3-.1-2.6-.4-3.8z" />
        <path fill="#388E3C" d="M10.3 14.7l6.6 4.8C18.7 16.1 21.1 14 24 14c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.6 14.5l3.7.2z" />
        <path fill="#F57C00" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-4.8C29.2 35.5 26.7 36.5 24 36.5c-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      </svg>
      {t("Continue with Google")}
    </button>
  );
}

function Field({ id, label, type, value, onChange, placeholder, testid, autoComplete }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block font-mono text-xs uppercase tracking-[0.15em] text-muted">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={onChange}
        data-testid={testid}
        className="w-full rounded-xl border border-line bg-surface px-4 py-3 text-sm outline-none transition-colors focus:border-accent"
      />
    </div>
  );
}

export { GoogleButton, Field };

export default function Login() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate(data.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title={t("Welcome back")} sub={t("Log in to open your Jobky workspace.")}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field id="email" label={t("Email")} type="email" value={email} onChange={(e) => setEmail(e.target.value)} testid="login-email" autoComplete="email" placeholder="you@example.com" />
        <Field id="password" label={t("Password")} type="password" value={password} onChange={(e) => setPassword(e.target.value)} testid="login-password" autoComplete="current-password" placeholder="••••••••" />
        {error && <p className="rounded-lg bg-bad/10 px-3 py-2 text-sm text-bad" data-testid="login-error">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="login-submit"
          className="w-full rounded-xl bg-accent px-4 py-3 text-sm font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
        >
          {busy ? t("Logging in…") : t("Log in")}
        </button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-muted">
        <span className="h-px flex-1 bg-line" /> {t("or")} <span className="h-px flex-1 bg-line" />
      </div>
      <GoogleButton />
      <div className="mt-6 flex justify-between text-sm">
        <Link to="/forgot-password" className="text-muted hover:text-accent" data-testid="login-forgot-link">{t("Forgot password?")}</Link>
        <Link to="/signup" className="font-bold text-accent" data-testid="login-signup-link">{t("Create account")}</Link>
      </div>
    </AuthShell>
  );
}
